/*
[GNU Affero General Public License (AGPL)](https://www.gnu.org/licenses/agpl-3.0.html)

VeloceUI - A UI component library for Angular v17+ made with care by Colibryx Srl in Italy.
Copyright (C) 2025 Colibryx S.r.l.

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as published by
the Free Software Foundation, either version 3 of the License, or
(at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

This license applies to any accompanying files located directly in the same directory.
*/

import { CommonModule } from "@angular/common";
import {
	Component,
	EventEmitter,
	Input,
	Output,
	ViewEncapsulation,
	forwardRef,
	input,
} from "@angular/core";
import { type ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import { IconComponent } from "@app/widgets/base/icon/icon.component";
import * as uuid from "uuid";

const noop = () => {};

const CUSTOM_INPUT_CONTROL_VALUE_ACCESSOR: any = {
	provide: NG_VALUE_ACCESSOR,
	useExisting: forwardRef(() => CheckboxComponent),
	multi: true,
};

@Component({
	selector: "cb-checkbox",
	standalone: true,
	imports: [CommonModule, IconComponent],
	templateUrl: "./checkbox.component.html",
	styleUrl: "./checkbox.component.scss",
	providers: [CUSTOM_INPUT_CONTROL_VALUE_ACCESSOR],
	encapsulation: ViewEncapsulation.None,
	// changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CheckboxComponent implements ControlValueAccessor {
	public readonly id: string = uuid.v1();

	public readonly size = input<"sm" | "md">("md");
	public readonly label = input<string>();
	public readonly helpText = input<string>();
	public readonly required = input(false);
	public readonly disabled = input(false);
	public readonly error = input(false);
	public readonly tripleState = input(false);
	public readonly readonly = input(false);
	@Input() public set selected(value: boolean | null) {
		this.value = value;
	}

	public readonly color = input<
		"brand" | "gray" | "error" | "warning" | "success" | "info"
	>("brand");

	@Output() public change: EventEmitter<boolean | null> = new EventEmitter<
		boolean | null
	>();

	@Output() public select: EventEmitter<{
		value: boolean;
		clickEvent: MouseEvent;
	}> = new EventEmitter<{ value: boolean; clickEvent: MouseEvent }>();

	//The internal data model
	private innerValue: boolean | null = false;

	public get value(): boolean | null {
		return this.innerValue;
	}

	public set value(v: boolean | null) {
		if (v !== this.innerValue) {
			this.innerValue = v;
			this.onChangeCallback(v);
			this.change.emit(v);
		}
	}

	public toggleValue(event: MouseEvent): void {
		if (this.readonly()) return;
		this.onTouchedCallback();

		if (this.tripleState()) {
			if (this.value === false) {
				this.value = null;
				return;
			}
			if (this.value === null) {
				this.value = true;
				return;
			}
		}

		this.value = !this.value;
		this.select.emit({ value: this.value, clickEvent: event });
	}

	public writeValue(obj: boolean): void {
		if (obj !== this.innerValue) {
			this.innerValue = obj;
		}
	}

	private onTouchedCallback: () => void = noop;
	private onChangeCallback: (_: any) => void = noop;

	// From ControlValueAccessor interface
	public registerOnChange(fn: any) {
		this.onChangeCallback = fn;
	}

	// From ControlValueAccessor interface
	public registerOnTouched(fn: any) {
		this.onTouchedCallback = fn;
	}
}
